import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import * as amplitude from '@amplitude/analytics-browser';

import type { ITimelineStep } from 'libs/design-system/src/types/timeline';
import { SkeletonLoader, Timeline } from '@unified-checkout/design-system';

import type { ITransferTimelineProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/transfers/transfer-timeline';
import type { ITransferProgressStep } from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer';
import { STEPS_STATUS } from 'libs/design-system/src/components/Timeline/utils';
import { TRANSFERS_STATUS } from '@app/dashboard/constants/balance_and_transfers';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { useTrackOnce } from '@app/dashboard/hooks/useTrackOnce';
import { getCurrentPath } from '@app/dashboard/utils/getCurrentPath';

import styles from './transfer-timeline.module.scss';

type StepStatus = (typeof STEPS_STATUS)[keyof typeof STEPS_STATUS];

const getStepStatus = (
  item: ITransferProgressStep,
  isFirstPending: boolean
): StepStatus => {
  if (
    [
      TRANSFERS_STATUS.FAILED,
      TRANSFERS_STATUS.REJECT_INSTANT,
      TRANSFERS_STATUS.RETURNED_BY_BANK,
    ].includes(item.key)
  )
    return STEPS_STATUS.FAILED;
  if (item.completed) return STEPS_STATUS.SUCCESS;
  if (isFirstPending) return STEPS_STATUS.PENDING;
  return STEPS_STATUS.DEFAULT;
};

const TransferTimeline = ({
  transferProgress = [],
  isLoading,
  header,
  entryPoint,
  settlementType,
  transferId,
}: ITransferTimelineProps) => {
  const { t } = useTranslation();

  const status = header?.status.key;

  const { formattedTimeline, stepDetails } = useMemo(() => {
    const timeline: ITimelineStep[] = [];
    let firstPendingFound = false;
    let details: ITransferProgressStep['detail'];

    transferProgress.forEach((item) => {
      const isFirstPending = !item.completed && !firstPendingFound;
      if (isFirstPending) {
        firstPendingFound = true;
      }

      if (!details && item.detail) {
        details = item.detail;
      }

      timeline.push({
        step: item.step,
        description: item.description,
        date: item.timestamp,
        status: getStepStatus(item, isFirstPending),
      });
    });

    return { formattedTimeline: timeline, stepDetails: details };
  }, [transferProgress]);

  useTrackOnce({
    trackFn: () => {
      amplitude.track(
        BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.TIMELINE_STEP_VIEWED,
        {
          [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.ENTRY_POINT]: entryPoint,
          [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.TRANSFER_ID]: transferId,
          [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE
            .SETTLEMENT_TYPE]: settlementType,
          [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE
            .CURRENT_STEP]: status,
          [SHARED_EVENTS_PARAMS.PAGE]: getCurrentPath(),
        }
      );
    },
    deps: [isLoading, transferId],
    shouldTrack: !isLoading && !!transferId,
  });

  return (
    <section
      className={styles['transfer-timeline']}
      data-timeline-status={status}
    >
      <p className={styles['transfer-timeline__title']}>
        {t('innerLayout.balanceTransfers.drawerDetails.transferProgress')}
      </p>
      <Timeline steps={formattedTimeline} isLoading={isLoading} />
      {isLoading ? (
        <SkeletonLoader width="100%" height="8rem" noOfRows={3} />
      ) : (
        stepDetails && (
          <div className={styles['transfer-timeline__banner']}>
            <div className={styles['transfer-timeline__banner-info']}>
              <p className={styles['transfer-timeline__banner-info-status']}>
                {stepDetails.title}
              </p>
              <p
                className={styles['transfer-timeline__banner-info-description']}
              >
                {stepDetails.message}
              </p>
            </div>
            {stepDetails?.bank_destination && (
              <div className={styles['transfer-timeline__banner-bank']}>
                <p className={styles['transfer-timeline__banner-bank-title']}>
                  {t(
                    'innerLayout.balanceTransfers.drawerDetails.bankDestination'
                  )}
                </p>
                <p className={styles['transfer-timeline__banner-bank-account']}>
                  {stepDetails.bank_destination}
                </p>
              </div>
            )}
          </div>
        )
      )}
    </section>
  );
};

export default TransferTimeline;
